/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import useIpLocation from '@src/hooks/useLocation';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { isArray } from 'lodash';

import ProcessHandler from '@src/components/ProcessHandler';
import {
  DURATION,
  MERGE_PRODUCT_DURATION,
  PACKAGE_DURATION_LABEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import api from '@src/apis';
import smallLogoTts from '@src/assets/logo/small-logo.svg';
import smallLogoApi from '@src/assets/logo/small-logo-api.svg';
import smallLogoDubbing from '@src/assets/logo/small-logo-dubbing.svg';
import {
  checkHasDiscountPackage,
  filterPackages,
  getMostPopularPackage,
  checkPackageDiscount,
} from '@src/services/package';
import { LANGUAGE, EVENT_TYPE } from '@src/constants';
import useElementWidth from '@src/hooks/useElementWidth';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import moment from 'moment';
import {
  checkVoucherByProduct,
  getSavedPrice,
  verifyPolicy,
  verifyTime,
} from '@src/services/voucher';
import { VOUCHER_TYPE } from '@src/constants/voucher';
import ROUTES from '@src/constants/route';
import { checkVietNam } from '@src/utils/checkCountry';
import { StyledPackages } from './index.style';
import PackageTypes from './PackageTypes';
import NewPackageItem from './NewPackageItem';
import SwiperVoucherBanner from './SwiperVoucherBanner';
import MobilePackageTypes from './MobilePackageTypes';

const STUDIO_BASIC_PACKAGE_CODE = 'STUDIO-BASIC';
const STUDIO_FREE_PACKAGE_CODE = 'STUDIO-FREE';
// const DUBBING_BASIC_PACKAGE_CODE = 'DUBBING-BASIC';
const DUBBING_TRIAL_PACKAGE_CODE = 'DUBBING-TRIAL';
const PACKAGE_RESPONSIVE_BREAKPOINT = 1167;
const PACKAGE_RESPONSIVE_MOBILE_BREAKPOINT = 600;

const NewPackages = () => {
  const theme = useTheme();
  const isDubbingRoute = useCheckDubbingRoute();
  const isVietNam = checkVietNam();
  const location = useLocation();
  const { ip } = useIpLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const getPackageType = () => {
    const packageTypeParam =
      queryParams.get('packageType') || PACKAGE_TYPE.STUDIO;
    return Object.values(PACKAGE_TYPE).includes(packageTypeParam)
      ? packageTypeParam
      : PACKAGE_TYPE.STUDIO;
  };

  const getPackageDuration = () => {
    const packageDurationParam = queryParams.get('duration') || DURATION.YEARLY;
    return Object.values(DURATION).includes(packageDurationParam)
      ? packageDurationParam
      : DURATION.YEARLY;
  };

  const initialPackageType = isDubbingRoute
    ? PACKAGE_TYPE.DUBBING
    : getPackageType();
  const initialPackageDuration = getPackageDuration();

  const [chosenPackage, setChosenPackage] = useState(null);
  const [packageType, setPackageType] = useState(
    isDubbingRoute ? PACKAGE_TYPE.DUBBING : initialPackageType,
  );

  const [packageDuration, setPackageDuration] = useState(
    initialPackageDuration,
  );
  const handleChangePackageDuration = (value) => {
    setPackageDuration(value);
    queryParams.set('duration', value);
    history.replace({ search: queryParams.toString() });
  };
  const [packages, setPackages] = useState([]);
  const [totalVoucher, setTotalVoucher] = useState([]);
  const [currentPackages, setCurrentPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayTrialPackage, setDisplayTrialPackage] = useState(false);
  const [saveUpToPercent, setSaveUpToPercent] = useState(0);
  const [hasDiscountPackage, setHasDiscountPackage] = useState(false);
  const [isFirstTimePaid, setIsFirstTimePaid] = useState(false);
  const [firstPaidVoucher, setFirstPaidVoucher] = useState(null);
  const [voucherBannerToShow, setVoucherBannerToShow] = useState([]);

  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const packagesRef = useRef();
  const packagesWidth = useElementWidth(packagesRef);

  const { usingPackage, latestOrder } = useSelector((state) => state.user);
  const { bannerVoucher } = useSelector((state) => state.voucher);
  const { user } = useSelector((state) => state.auth);

  const { getFeatureValue } = useFeatureFlags();

  const promotedMoePackageCode = getFeatureValue(
    FEATURE_KEYS.PROMOTED_PACKAGE_MOE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const discountForFirstPaidUser = getFeatureValue(
    FEATURE_KEYS.DISCOUNT_FOR_FIRST_PAID_USER,
  );

  const isPushEventViewPricing = getFeatureValue(
    FEATURE_KEYS.PUSH_EVENT_VIEW_PRICING,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useRenameMergeProduct = getFeatureValue(
    FEATURE_KEYS.RENAME_MERGE_PRODUCT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const useVoucherByRegion = getFeatureValue(FEATURE_KEYS.NEW_PRICE_VOUCHER, {
    ip,
  });

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // this feature to move DUBBING packages outside on MOBILE (on the same place with STUDIO and API)
  const useNewPackageMobile = isMobile;

  // this feature to move DUBBING packages outside on DESKTOP (on the same place with STUDIO and API)
  const useNewPackagesTab = getFeatureValue(FEATURE_KEYS.NEW_PACKAGES_TAB, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const handleChangePackageType = (type) => {
    setPackageType(type);
    setPackageDuration(DURATION.YEARLY);
    queryParams.set('packageType', type);
    queryParams.set('duration', DURATION.YEARLY);
    if (useNewPackagesTab) {
      history.push(ROUTES.PAYMENT);
    }
    history.replace({ search: queryParams.toString() });
  };

  const getHighlightPackageCode = (pkgs) => {
    const popularPackage = getMostPopularPackage(pkgs);
    return popularPackage?.code;
  };

  const packageTypeTabs = [
    {
      label: 'PKG_STUDIO',
      id: PACKAGE_TYPE.STUDIO,
    },
    { label: 'PKG_API', id: PACKAGE_TYPE.API },
  ];

  const newPackageTypeTabs = [
    {
      label: 'PKG_STUDIO',
      id: PACKAGE_TYPE.STUDIO,
      logo: smallLogoTts,
    },
    { label: 'PKG_API', id: PACKAGE_TYPE.API, logo: smallLogoApi },
    { label: 'DUBBING', id: PACKAGE_TYPE.DUBBING, logo: smallLogoDubbing },
  ];

  const mergeProductPackageTypeTabs = [
    {
      label: 'AIVOICE_STUDIO',
      id: PACKAGE_TYPE.STUDIO,
      logo: smallLogoTts,
    },
    { label: 'AIVOICE_API', id: PACKAGE_TYPE.API, logo: smallLogoApi },
  ];

  const renderPackageTypeTabs = () => {
    // if feature merge product is ON and it is MOBILE
    if (useRenameMergeProduct && isMobile) {
      return (
        <MobilePackageTypes
          types={mergeProductPackageTypeTabs}
          activeType={packageType}
          onChangeType={handleChangePackageType}
        />
      );
    }

    // if feature merge product is ON and it is DESKTOP
    if (useRenameMergeProduct && !isMobile) {
      return (
        <PackageTypes
          types={mergeProductPackageTypeTabs}
          activeType={packageType}
          onChangeType={handleChangePackageType}
          useRenameMergeProduct={useRenameMergeProduct}
          useNewPackagesTab={useNewPackagesTab}
        />
      );
    }

    // if feature that move DUBBING packages outside is ON and it is MOBILE
    if (useNewPackageMobile) {
      return (
        <MobilePackageTypes
          types={newPackageTypeTabs}
          activeType={packageType}
          onChangeType={handleChangePackageType}
        />
      );
    }

    // if feature that move DUBBING packages outside is ON and it is DESKTOP
    if (useNewPackagesTab) {
      return (
        <PackageTypes
          types={newPackageTypeTabs}
          activeType={packageType}
          onChangeType={handleChangePackageType}
          useNewPackagesTab={useNewPackagesTab}
        />
      );
    }

    if (isDubbingRoute)
      return (
        <Typography className="package-type-label">{t('priceList')}</Typography>
      );

    return (
      <PackageTypes
        types={packageTypeTabs}
        activeType={packageType}
        onChangeType={handleChangePackageType}
      />
    );
  };

  const handleSetCurrentPackages = (duration, allPackages) => {
    const displayBasicPackage =
      packageType === PACKAGE_TYPE.API ||
      packageType === PACKAGE_TYPE.DUBBING ||
      !displayTrialPackage;

    const displayPackages = filterPackages({
      duration,
      data: allPackages,
      packageType,
      displayTrialPackage,
      displayBasicPackage,
      // to handle that case: http://localhost:3000/studio/payments?packageType=DUBBING&duration=YEARLY
      // in this above case, isDubbingRoute will be false, so it will not show DUBBING_TRIAL
      isDubbingPackages: useNewPackagesTab || isDubbingRoute,
    });
    setCurrentPackages(displayPackages?.sort((a, b) => a.rank - b.rank));
    setSaveUpToPercent(displayPackages[1]?.yearDiscount || null);
    const isHasDiscountPackage = checkHasDiscountPackage(displayPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleDiscountEnd = () => {
    const isHasDiscountPackage = checkHasDiscountPackage(currentPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const fetchPackages = async () => {
    setLoading(true);
    const pkgQuery = { type: packageType, active: true, purchasable: true };
    if (useRenameMergeProduct && packageType === PACKAGE_TYPE.DUBBING) {
      pkgQuery.type = PACKAGE_TYPE.STUDIO;
    }

    const { status, result } = await api.packages.getPackages(pkgQuery);
    setLoading(false);
    if (status) setPackages(result.packages);
  };

  const fetchVoucherForFirstPaidUser = async () => {
    const response = await api.vouchers.getVoucherForFirstPaidOrder();
    if (response.status) {
      const voucher = response.result?.[0];
      const satisfiedTime = verifyTime(voucher);
      if (satisfiedTime) setFirstPaidVoucher(voucher);
    }
  };

  const fetchTotalVouchers = async () => {
    const data = await api.vouchers.getVouchers();
    if (data.status) {
      setTotalVoucher(data.result);
    }
  };

  const filterUseableVoucher = (currPackageId) =>
    totalVoucher
      .filter((voucher) => {
        const satisfiedTime = verifyTime(voucher);
        const satisfiedPolicy = verifyPolicy(voucher, currPackageId);

        const isValidVoucherTypeInCountry =
          useVoucherByRegion ||
          isVietNam ||
          voucher.type !== VOUCHER_TYPE.PRICE;

        const isPriceOrPercentVoucher =
          voucher.type === VOUCHER_TYPE.PERCENT ||
          voucher.type === VOUCHER_TYPE.PRICE;

        const canUse =
          satisfiedTime &&
          satisfiedPolicy &&
          voucher.remainingAmount &&
          !voucher.isPrivate &&
          voucher.usedAmount < voucher.numReuse &&
          isValidVoucherTypeInCountry &&
          isPriceOrPercentVoucher;

        return canUse;
      })
      .map((voucher) => ({
        ...voucher,
        satisfiedTime: verifyTime(voucher),
        satisfiedPolicy: verifyPolicy(voucher, currPackageId),
        canUse: true,
      }))
      .sort((a) => (a.canUse ? -1 : 1));

  const checkShowBannerVoucherNew = () => {
    if (!bannerVoucher || !bannerVoucher.length) return false;
    const result = bannerVoucher.filter((voucher) => {
      const { endTime } = voucher;
      const isValidTime = endTime && moment().isBefore(endTime);
      if (!isValidTime) return false;

      const { applyAllPackages, packages: validPackages } =
        voucher?.policy || {};
      if (applyAllPackages) return true;

      return validPackages.some(
        (pkg) => pkg.type.toLowerCase() === packageType.toLowerCase(),
      );
    });

    return result;
  };

  // Show package highlight in the first row when responsive
  const packageSort = (pkg1, pkg2) => {
    const { code: code1, discountConfig: discountConfig1 } = pkg1;
    const { code: code2, discountConfig: discountConfig2 } = pkg2;
    const highlightPackage = getHighlightPackageCode(currentPackages);
    if ([STUDIO_BASIC_PACKAGE_CODE, STUDIO_FREE_PACKAGE_CODE].includes(code1))
      return -1;
    if ([STUDIO_BASIC_PACKAGE_CODE, STUDIO_FREE_PACKAGE_CODE].includes(code2))
      return 1;

    // if (code1 === DUBBING_BASIC_PACKAGE_CODE) return -1;
    // if (code2 === DUBBING_BASIC_PACKAGE_CODE) return 1;
    if (code1 === DUBBING_TRIAL_PACKAGE_CODE) return -1;
    if (code2 === DUBBING_TRIAL_PACKAGE_CODE) return 1;

    if (code1 === highlightPackage) return -1;
    if (code2 === highlightPackage) return 1;

    if (
      checkPackageDiscount(discountConfig1) &&
      !checkPackageDiscount(discountConfig2)
    )
      return -1;
    if (
      !checkPackageDiscount(discountConfig1) &&
      checkPackageDiscount(discountConfig2)
    )
      return 1;

    return 0;
  };

  const packageSortMobile = (pkg1, pkg2) => {
    const { code: code1, discountConfig: discountConfig1 } = pkg1;
    const { code: code2, discountConfig: discountConfig2 } = pkg2;
    const highlightPackage = getHighlightPackageCode(currentPackages);
    if ([STUDIO_BASIC_PACKAGE_CODE, STUDIO_FREE_PACKAGE_CODE].includes(code1))
      return 1;
    if ([STUDIO_BASIC_PACKAGE_CODE, STUDIO_FREE_PACKAGE_CODE].includes(code2))
      return -1;

    // if (code1 === DUBBING_BASIC_PACKAGE_CODE) return -1;
    // if (code2 === DUBBING_BASIC_PACKAGE_CODE) return 1;
    if (code1 === DUBBING_TRIAL_PACKAGE_CODE) return 1;
    if (code2 === DUBBING_TRIAL_PACKAGE_CODE) return -1;

    if (code1 === highlightPackage) return -1;
    if (code2 === highlightPackage) return 1;

    if (
      checkPackageDiscount(discountConfig1) &&
      !checkPackageDiscount(discountConfig2)
    )
      return 1;
    if (
      !checkPackageDiscount(discountConfig1) &&
      checkPackageDiscount(discountConfig2)
    )
      return -1;

    return 0;
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const getUsingPackage = (pkgType) => {
    switch (pkgType) {
      case PACKAGE_TYPE.STUDIO: {
        const studioPackage = packages.find(
          (pkg) => pkg.code === user?.packageCode,
        );
        return {
          ...studioPackage,
          isExpires: checkExpiredDate(user?.packageExpiryDate),
        };
      }
      case PACKAGE_TYPE.API: {
        const apiPackage = packages.find(
          (pkg) => pkg.code === user?.apiPackage?.packageCode,
        );
        return {
          ...apiPackage,
          isExpires: checkExpiredDate(user?.apiPackage?.packageExpiryDate),
        };
      }
      case PACKAGE_TYPE.DUBBING: {
        const dubbingPackage = packages.find(
          (pkg) => pkg.code === user?.dubbing?.packageCode,
        );
        return {
          ...dubbingPackage,
          isExpires: checkExpiredDate(user?.dubbing?.packageExpiryDate),
        };
      }
      default:
        return null;
    }
  };

  const handleChoosePackageMobile = (pkg) => {
    if (!useNewPackageMobile) return;
    if (chosenPackage && chosenPackage.id === pkg.id) {
      setChosenPackage(null);
    } else {
      setChosenPackage(pkg);
    }
  };

  const renderFlagUI = (item) => (
    <NewPackageItem
      isPushEventViewPricing={isPushEventViewPricing}
      chosenPackage={chosenPackage}
      handleChoosePackageMobile={handleChoosePackageMobile}
      data={item}
      duration={packageDuration}
      hasDiscountPackage={hasDiscountPackage}
      onDiscountEnd={handleDiscountEnd}
      isFirstTimePaid={isFirstTimePaid}
      discountForFirstPaidUser={discountForFirstPaidUser}
      firstPaidVoucher={firstPaidVoucher}
      canUseVouchers={filterUseableVoucher(item?.id)}
      currentUsingPackage={getUsingPackage(item?.type)}
      useNewPackageMobile={useNewPackageMobile}
    />
  );

  const renderPackages = (pkgs = []) => pkgs.map((item) => renderFlagUI(item));

  const checkUserFirstTimePaid = async () => {
    const payFees = 'pay_fees';
    const response = await api.orders.getUserPaidOrders(payFees);
    if (response.status && response.result?.orders.length < 1)
      if (discountForFirstPaidUser) setIsFirstTimePaid(true);
  };

  const filterVoucherBannerByProduct = () => {
    const result =
      isArray(bannerVoucher) &&
      bannerVoucher.length > 0 &&
      bannerVoucher.filter((voucher) => {
        const check = checkVoucherByProduct({ voucher, product: packageType });
        return check;
      });

    return result;
  };

  useEffect(() => {
    checkUserFirstTimePaid();
  }, [discountForFirstPaidUser]);

  useEffect(() => {
    fetchVoucherForFirstPaidUser();
    fetchTotalVouchers();
    return () => {
      setFirstPaidVoucher(null);
      setTotalVoucher([]);
    };
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [packageType, useRenameMergeProduct]);

  useEffect(() => {
    const result = filterVoucherBannerByProduct();
    setVoucherBannerToShow(result);
  }, [bannerVoucher, packageType]);

  useEffect(() => {
    handleSetCurrentPackages(packageDuration, packages);
  }, [usingPackage, packageDuration, packages]);

  useEffect(() => {
    if (latestOrder.id) setDisplayTrialPackage(false);
    else setDisplayTrialPackage(true);
  }, [latestOrder]);

  useEffect(() => {
    if (!promotedMoePackageCode || !packages || !totalVoucher.length) return;

    const promotedPackage = packages.find(
      (pkg) => pkg.code === promotedMoePackageCode,
    );
    if (!promotedPackage) return;

    const validVouchers = filterUseableVoucher(promotedPackage?.id);
    const { price, usdPrice } = promotedPackage;

    let isDiscountPackage = false;
    const { startAt, endAt } = promotedPackage.discountConfig || {};
    const inDiscountTime = startAt && moment().isBetween(startAt, endAt);

    if (inDiscountTime) isDiscountPackage = true;
    else isDiscountPackage = false;

    const listVoucherSortByRanking = validVouchers
      .map((voucherItem) => {
        const reducedPrice = getSavedPrice({
          voucher: voucherItem,
          currPrice: isDiscountPackage
            ? promotedPackage?.discountConfig?.price || price
            : price,
          currUsdPrice: isDiscountPackage
            ? promotedPackage?.discountConfig?.usdPrice || usdPrice
            : usdPrice,
        });
        return { ...voucherItem, reducedPrice };
      })
      .sort((a, b) => b.reducedPrice - a.reducedPrice);

    let bestPromotedVoucher;
    if (listVoucherSortByRanking.length > 0)
      [bestPromotedVoucher] = listVoucherSortByRanking;

    const plan = promotedPackage?.code?.split('-')[1];

    window.dataLayer.push({
      event: EVENT_TYPE.VIEW_PRICING,
      userId: user?.id,
      email: user.email,
      product: packageType,
      plan,
      period: packageDuration,
      promoted_package_price: promotedMoePackageCode?.price,
      promoted_package_code: promotedMoePackageCode,
      promoted_package_voucher_code: bestPromotedVoucher?.code,
      promoted_package_voucher_type: bestPromotedVoucher?.type,
      promoted_package_voucher_amount: bestPromotedVoucher?.value,
    });
  }, [packages, promotedMoePackageCode, totalVoucher]);

  return (
    <StyledPackages
      isVietNam={language === LANGUAGE.VN}
      num={currentPackages.length}
      useNewPackageMobile={useNewPackageMobile}
      useRenameMergeProduct={useRenameMergeProduct}
      chosenPackage={chosenPackage}
    >
      {/* package tab */}
      {renderPackageTypeTabs()}
      {/* duration tab */}
      <div className="duration-header">
        <div className="tab-group-wrapper">
          {Object.values(
            useRenameMergeProduct ? MERGE_PRODUCT_DURATION : DURATION,
          ).map((value) => (
            <div
              className={
                packageDuration === value
                  ? `tabs-active`
                  : `${value === DURATION.YEARLY ? 'pr-4' : ''} tabs`
              }
              onClick={() => handleChangePackageDuration(value)}
              role="button"
              tabIndex={0}
              data-id="select-pricing-duration"
              data-duration={value?.toLocaleLowerCase()}
            >
              {t(PACKAGE_DURATION_LABEL[value])}
              {value === DURATION.YEARLY && saveUpToPercent ? (
                <div className="save">
                  {t('saveUpTo', {
                    percent: saveUpToPercent,
                  }).toUpperCase()}
                </div>
              ) : (
                <div className="no-save" />
              )}
            </div>
          ))}
        </div>

        {!isDubbingRoute && packageDuration !== DURATION.YEARLY ? (
          <div className="discount-year-mobile">
            {t('buyYearSaveUpTo', { percent: saveUpToPercent })}
          </div>
        ) : (
          <div className="discount-year-height" />
        )}
      </div>
      {/* swiper pricing banner */}
      {checkShowBannerVoucherNew() &&
        voucherBannerToShow.length > 0 &&
        !useNewPackageMobile && (
          <div className="packages-wrapper">
            <SwiperVoucherBanner bannerVoucher={voucherBannerToShow} />
          </div>
        )}
      {/* list packages */}
      <div ref={packagesRef} className="packages-wrapper">
        <ProcessHandler loading={loading} mt="30px" mb="30px" align="center">
          <div className="packages">
            {packagesWidth > PACKAGE_RESPONSIVE_BREAKPOINT
              ? renderPackages(currentPackages)
              : packagesWidth > PACKAGE_RESPONSIVE_MOBILE_BREAKPOINT
              ? renderPackages(currentPackages?.slice()?.sort(packageSort))
              : renderPackages(
                  currentPackages?.slice()?.sort(packageSortMobile),
                )}
          </div>
        </ProcessHandler>
      </div>
      {/* policy note */}

      <div className="notice-ai-voice-container">
        <span className="notice-ai-voice">{t('noticeAIVoice1')}</span>
        <span className="notice-ai-voice">{t('noticeAIVoice2')}</span>
      </div>
    </StyledPackages>
  );
};

export default NewPackages;
